/**
 * sections.tsx — card-content layout primitives shared by EVERY authed surface,
 * staff and patient alike: a section heading, a label/value row, and a
 * responsive card grid.
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHY here (neutral `ui/`) and not `staff/ui.tsx`: these are role-agnostic
 * presentational atoms — the patient dashboard/profile use the SAME grammar as
 * the clinician/admin consoles. They lived in `staff/ui.tsx` historically;
 * that module now RE-EXPORTS them from here so the ~20 staff imports are
 * unchanged, while patient pages import from `ui/` directly (no more per-page
 * copies). Every value is a `var(--gps-*)` token so light/dark just work.
 */
import type { CSSProperties, ReactNode } from 'react';

const HEADING_STYLE: CSSProperties = {
  fontFamily: 'var(--gps-font-display)',
  fontSize: '17px',
  lineHeight: 1.25,
  letterSpacing: '-0.01em',
  margin: 0,
  color: 'var(--gps-text)',
};

/** A display-face section heading inside a card (standalone — no trailing action). */
export function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 style={{ ...HEADING_STYLE, margin: '0 0 14px' }}>{children}</h2>;
}

/**
 * The same heading as {@link SectionTitle} with NO trailing margin, for a title
 * that SHARES ITS LINE with controls.
 *
 * ⚠️ IT EXISTS TO KILL A NEGATIVE MARGIN, not to offer a choice. `SectionTitle`
 * bakes in `margin: 0 0 14px`, which is right when the heading owns its own
 * line and wrong the moment it becomes a flex item: the 14px is part of the
 * item's margin box, so it pushes the title up off the line it is supposed to
 * be sharing, and the only local fix is a `-14px` somewhere that silently rots
 * the day this constant changes. {@link CardHeader} already solves it the right
 * way round — heading at margin 0, the ROW owns the spacing below it — and this
 * is that same split, exposed for callers building their own row.
 */
export function SectionTitleInline({ children }: { children: ReactNode }) {
  return <h2 style={HEADING_STYLE}>{children}</h2>;
}

/**
 * A card header: the display-face title (optional muted subtitle) on the left and
 * an optional `action` (a status Badge, a link, a button) on the right, baseline-
 * aligned. Use this instead of a bare flex row so the title+badge pattern stays
 * consistent across every card (2026-07-25 modern-clean).
 */
export function CardHeader({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--gps-s-3)', margin: '0 0 14px' }}>
      <div style={{ minWidth: 0 }}>
        <h2 style={HEADING_STYLE}>{title}</h2>
        {subtitle ? <p style={{ margin: '3px 0 0', fontSize: '13px', color: 'var(--gps-muted)' }}>{subtitle}</p> : null}
      </div>
      {action ? <div style={{ flex: 'none' }}>{action}</div> : null}
    </div>
  );
}

/**
 * A card header with a soft ICON CHIP beside the title (patient surfaces,
 * 2026-07-26 modern pass). The rounded tinted chip gives each card a clear,
 * scannable identity; `action` (a status badge) sits baseline-right. Additive —
 * the staff consoles keep the plain {@link CardHeader}. Icons are decorative.
 */
export function CardIconHeader({
  icon,
  title,
  subtitle,
  action,
}: {
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    // flex-wrap + row-gap so a status badge drops neatly below the title on a
    // narrow (mobile) card instead of overflowing or crushing the heading.
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--gps-s-2) var(--gps-s-3)', flexWrap: 'wrap', margin: '0 0 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
        <span
          aria-hidden="true"
          style={{
            display: 'grid',
            placeItems: 'center',
            width: '36px',
            height: '36px',
            flex: 'none',
            borderRadius: 'var(--gps-r-md)',
            background: 'var(--gps-primary-light)',
            color: 'var(--gps-green-text)',
          }}
        >
          {icon}
        </span>
        {/* Title + optional metadata subtitle stacked, aligned past the icon. */}
        <div style={{ minWidth: 0 }}>
          <h2 style={{ ...HEADING_STYLE, fontSize: '18.5px' }}>{title}</h2>
          {subtitle ? <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--gps-muted)' }}>{subtitle}</p> : null}
        </div>
      </div>
      {action ? <div style={{ flex: 'none' }}>{action}</div> : null}
    </div>
  );
}

/**
 * A label/value row. Rows stack with a hairline divider BETWEEN them (the
 * `.gps-kv-row:last-child` rule in tokens.css drops the trailing line so a card
 * never ends on a dangling divider — the modern-clean detail).
 */
export function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div
      className="gps-kv-row"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 'var(--gps-s-3)',
        padding: '10px 0',
        fontSize: '16px',
      }}
    >
      <span style={{ color: 'var(--gps-muted)' }}>{label}</span>
      <span style={{ color: 'var(--gps-text)', fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

/** A responsive grid of cards (auto-fit columns). */
export function CardGrid({ children, min = '320px', style }: { children: ReactNode; min?: string; style?: CSSProperties }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)', gridTemplateColumns: `repeat(auto-fit, minmax(${min}, 1fr))`, marginTop: 'var(--gps-s-3)', ...style }}>
      {children}
    </div>
  );
}
